import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import API from "../shared-utils/api";
import { useCustomer } from "../contexts/CustomerProvider";
export const useCastVote = () => {
    const navigate = useNavigate();
    const { customerData } = useCustomer();
    const toast = useToast();
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const [participantName, setParticipantName] = useState("");
    // Cleanup effect to reset success screen state
    useEffect(() => {
        return () => {
            // Reset success screen state when component unmounts
            setShowSuccessScreen(false);
            setParticipantName("");
        };
    }, []);
    const mutation = useMutation(async (params) => {
        const response = await API.post("/api/v1/rbzo/votecast/vote", params);
        // Check if the response indicates an error
        if (response.data.status_code === "DENIED" ||
            response.data.status_code === "FORBIDDEN") {
            throw new Error(JSON.stringify(response.data));
        }
        return response.data;
    }, {
        onSuccess: (data) => {
            // Show success screen instead of toast
            setShowSuccessScreen(true);
        },
        onError: (error) => {
            var _a, _b;
            console.error("Error casting vote:", error);
            let errorData;
            try {
                // Try to parse error message if it's a JSON string
                if (error.message) {
                    errorData = JSON.parse(error.message);
                }
                else {
                    errorData = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data;
                }
            }
            catch (e) {
                errorData = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data;
            }
            console.log("Error data:", errorData);
            // Check if it's the "already voted" error
            if ((errorData === null || errorData === void 0 ? void 0 : errorData.status_code) === "DENIED" &&
                (errorData === null || errorData === void 0 ? void 0 : errorData.status_message) === "You have already voted in this event") {
                toast({
                    title: "Already Voted",
                    description: "You have already voted for this event.",
                    status: "warning",
                    duration: 4000,
                    isClosable: true,
                    position: "top",
                });
            }
            else if ((errorData === null || errorData === void 0 ? void 0 : errorData.status_code) === "FORBIDDEN" &&
                (errorData === null || errorData === void 0 ? void 0 : errorData.status_message) === "Invalid user authentication") {
                toast({
                    title: "Login Required",
                    description: "Please log in to cast your vote.",
                    status: "info",
                    duration: 2000,
                    isClosable: true,
                    position: "top",
                });
                // Redirect to login with current page as redirect
                const currentPath = window.location.pathname + window.location.search;
                const encodedRedirect = encodeURIComponent(currentPath);
                navigate(`/verify-phone/direct?redirect=${encodedRedirect}`);
            }
            else {
                // Generic error toast
                toast({
                    title: "Vote Failed",
                    description: "Failed to cast your vote. Please try again.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                    position: "top",
                });
            }
        },
    });
    const castVote = async (params) => {
        // Check if user is logged in by checking localStorage
        const isLoggedIn = localStorage.getItem("is_phone_verified") === "true";
        if (!isLoggedIn) {
            // Store voting data in localStorage for retrieval after login
            const votingDataI = {
                whatsHappeningId: params.whatsHappeningId,
                participantType: params.participantType,
                participantId: params.participantId,
                participantName: params.participantName,
                timestamp: Date.now(),
            };
            localStorage.setItem("voting_data", JSON.stringify({
                votingData: params.votingData,
                timestamp: Date.now(),
            }));
            localStorage.setItem("pending_vote_data", JSON.stringify(votingDataI));
            // Redirect to login page with current path as redirect
            const currentPath = window.location.pathname + window.location.search;
            const encodedRedirect = encodeURIComponent(currentPath);
            navigate(`/verify-phone/direct?redirect=${encodedRedirect}`);
            return;
        }
        // Store participant name for success screen
        if (params.participantName) {
            setParticipantName(params.participantName);
        }
        // If logged in, proceed with vote casting
        mutation.mutate(params);
    };
    return {
        castVote,
        isLoading: mutation.isLoading,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
        showSuccessScreen,
        setShowSuccessScreen,
        participantName,
        setParticipantName,
    };
};
